import { useDispatch } from "react-redux";
import { Container, Col, Row, Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useMemo, type ComponentProps } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { SXModalBase } from "./SXModal";
import { useTranslation } from "react-i18next";
import { closeModal } from "../../features/ui/uiSlice";
import "../../../i18n";
import { ng } from "@common/ngds";
import { SXEditField, type UnitConverterType } from "@common/components/ui-components/SXEditField";
import { useProject } from "../../features/projects/projectSlice";
import { useUnit } from "../../features/unit/unitSlice";
import DualListBox from "react-dual-listbox";
import { dualListBoxIcons } from "../ui-components/SXDualListBoxIcons";
import { report_exception } from "../ui-components/SXAlert/SXAlertMessage";

import "react-dual-listbox/lib/react-dual-listbox.css";
import SXTwoWaySwitch from "@common/components/ui-components/SXTwoWaySwitch";
import { SXSpin } from "@common/components/ui-components/SXSpin";

interface LCOGeneratoreModalProps {
  isModalOpen: boolean;
}

export const LCOGeneratorModalId = "LCOGenerator";

type ValueType = string;

const LOAD_COMBINATION_TYPES = [
  { name: "type_characteristic_", id: ng.loadcombination.Type.TYPE_CHARACTERISTIC },
  { name: "type_frequent_", id: ng.loadcombination.Type.TYPE_FREQUENT },
  { name: "type_quasi_permanent_", id: ng.loadcombination.Type.TYPE_QUASI_PERMANENT },
  { name: "type_ultimate_", id: ng.loadcombination.Type.TYPE_ULTIMATE },
  { name: "type_accidental_", id: ng.loadcombination.Type.TYPE_ACCIDENTAL },
  { name: "type_fire_", id: ng.loadcombination.Type.TYPE_FIRE },
  { name: "type_seismic_", id: ng.loadcombination.Type.TYPE_SEISMIC },
];

type DualListBoxLang = NonNullable<ComponentProps<typeof DualListBox>["lang"]>;

const dualListLang = (t: (key: string) => string): DualListBoxLang => ({
  availableHeader: t("lco_generator_.available_"),
  selectedHeader: t("lco_generator_.chosen_"),
});

// The four settings on the optional row are national-annex parameters whose labels are bare
// clause numbers or psi symbols - meaningless unless you already know the Eurocode - so each
// carries an explanation. It used to be a hover trigger on the whole <Col>, but that flickered
// (the overlay covered the input it was describing, so moving onto the tooltip to read it left
// the trigger area, closing it) and sat on top of the field. Same pattern as the fire settings'
// question-mark icon instead: a small dedicated button next to the label is the only trigger, so
// the control underneath stays clickable and the tooltip never overlaps it.
//
// trigger="click" + rootClose, not hover: three of these four icons sit right next to another
// hover-reactive sibling (the label text row, or - for the 610ab switch - inside the Form.Switch
// label itself), and a hover-triggered overlay re-fires open/close on every minor layout
// recalculation nearby, which is what still flickered even after narrowing the trigger down to
// just the icon. Click + rootClose is the same stable pattern already used for the equivalent
// info buttons in SteelFireSettings.tsx: it only opens on an explicit click and only closes on an
// outside click, so there is no continuous hover state to race.
const InfoIcon = (props: { id: string; text: string }) => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom"
    overlay={
      <Tooltip id={props.id} className="sx-tooltip-wide">
        {props.text}
      </Tooltip>
    }
  >
    <button
      type="button"
      aria-label={props.text}
      className="info-button"
      style={{
        border: "1px solid #DFDFDF",
        background: "transparent",
        boxShadow: "none",
        WebkitAppearance: "none",
        appearance: "none",
        marginLeft: 6,
      }}
    >
      <FontAwesomeIcon icon={faQuestion} style={{ fontSize: 10 }} />
    </button>
  </OverlayTrigger>
);

export const LCOGeneratoreModal = (props: LCOGeneratoreModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const pp = useProject();

  const projectJson = pp.project?.data.data?.input;
  const cacheJson = pp.project?.data.settings?.endpointCache;

  const project = projectJson ? ng.input.Data.fromObject(projectJson) : null;
  const cache = cacheJson ? ng.frontend.column.EndPointParamCache.fromObject(cacheJson) : null;

  const ec = project?.getEc();
  const structure = project?.structures?.[0] ?? undefined;
  const loading = structure?.loading;
  const genDesSet_load = structure?.designSettings?.load;

  // The snow load is held in SI (N/m²) and shown per the user's force/length units, which is kN/m²
  // by default. The unit is not a guess: sx-eurocode-api reads this field straight through to the
  // psi lookup (PhiForLoadsG1/G2.GetPsihSwedish, GetPsihFinnish) and thresholds it at 2000 and 3000
  // for Sweden and 2750 for Finland - i.e. the EKS 2,0/3,0 kN/m² and Finnish 2,75 kN/m² bands, in
  // N/m². Until now the field carried no unit at all - the converter above is a stub returning
  // null - so it showed the raw SI number with nothing to say what it was.
  const {
    toDisplaySurfaceLoad,
    fromDisplaySurfaceLoad,
    surfaceLoadUnit,
    toDisplayLength,
    fromDisplayLength,
    lengthUnit,
  } = useUnit();
  const snowLoadUnit: UnitConverterType = useMemo(
    () => ({
      toInput: (value: number) => toDisplaySurfaceLoad(Number(value)),
      fromInput: (value: number) => fromDisplaySurfaceLoad(Number(value)),
      getDisplayString: () => surfaceLoadUnit,
    }),
    [toDisplaySurfaceLoad, fromDisplaySurfaceLoad, surfaceLoadUnit],
  );

  // The site altitude, in metres above sea level. Same story as the snow load: sx-eurocode-api
  // thresholds this field at `cenHeight > 1000` (PhiForLoadsG1/G2, for the Common and Great Britain
  // annexes that show it) - the EN 1990 "above 1000 m" snow rule - so the stored value is metres,
  // which is the length unit's SI base and therefore an identity conversion today.
  const altitudeUnit: UnitConverterType = useMemo(
    () => ({
      toInput: (value: number) => toDisplayLength(Number(value)),
      fromInput: (value: number) => fromDisplayLength(Number(value)),
      getDisplayString: () => lengthUnit,
    }),
    [toDisplayLength, fromDisplayLength, lengthUnit],
  );

  // ============================================================================================
  // conditionals

  const showAltitude = (() => {
    let currentAnnex: ng.eurocode.Annex =
      project?.code?.type === "ec" ? project.code.ec.nationalAnnex : ng.eurocode.Annex.ANNEX_UNSPECIFIED;
    // Not allowed for Finnish, Swedish, Danish, and norwegian national annexes
    if (
      currentAnnex === ng.eurocode.Annex.ANNEX_FINLAND ||
      currentAnnex === ng.eurocode.Annex.ANNEX_SWEDEN ||
      currentAnnex === ng.eurocode.Annex.ANNEX_DENMARK ||
      currentAnnex === ng.eurocode.Annex.ANNEX_NORWAY
    ) {
      return false;
    } else {
      return true;
    }
  })();

  const showSnowLoad = (() => {
    let currentAnnex: ng.eurocode.Annex =
      project?.code?.type === "ec" ? project.code.ec.nationalAnnex : ng.eurocode.Annex.ANNEX_UNSPECIFIED;
    // Only allowed for Finnish and Swedish national annexes
    if (currentAnnex === ng.eurocode.Annex.ANNEX_FINLAND || currentAnnex === ng.eurocode.Annex.ANNEX_SWEDEN) {
      return true;
    } else {
      return false;
    }
  })();

  const showPsi1 = (() => {
    let currentAnnex: ng.eurocode.Annex =
      project?.code?.type === "ec" ? project.code.ec.nationalAnnex : ng.eurocode.Annex.ANNEX_UNSPECIFIED;
    // Not allowed for Danish, English and Finnish national annexes
    if (
      currentAnnex === ng.eurocode.Annex.ANNEX_DENMARK ||
      currentAnnex === ng.eurocode.Annex.ANNEX_GREAT_BRITAIN ||
      currentAnnex === ng.eurocode.Annex.ANNEX_FINLAND
    ) {
      return false;
    } else {
      return true;
    }
  })();

  const show610ab = (() => {
    let show = false;

    let currentAnnex: ng.eurocode.Annex =
      project?.code?.type === "ec" ? project.code.ec.nationalAnnex : ng.eurocode.Annex.ANNEX_UNSPECIFIED;

    // Not allowed for Danish, Swedish, Norwegian and Finnish national annexes
    if (
      currentAnnex === ng.eurocode.Annex.ANNEX_DENMARK ||
      currentAnnex === ng.eurocode.Annex.ANNEX_SWEDEN ||
      currentAnnex === ng.eurocode.Annex.ANNEX_NORWAY ||
      currentAnnex === ng.eurocode.Annex.ANNEX_FINLAND
    ) {
      show = false;
    } else {
      show = true;
    }

    let gen = project?.code?.type === "ec" ? project.code.ec.generation : ng.eurocode.Generation.GENERATION_1;
    if (show && gen === ng.eurocode.Generation.GENERATION_2) {
      show = false; // Not allowed for Eurocode 2
    }

    return show;
  })();

  // ============================================================================================
  // update functions

  const update_alt = async (val: number) => {
    const label = t("lco_generator_.altitude_");
    try {
      await pp.lcoGenerator_updateAlt(val);
    } catch (x) {
      report_exception(t, x, label);
    }
  };
  const update_snowload = async (a: number) => {
    try {
      await pp.lcoGenerator_update_snowload(a);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };
  const update_use610ab = async (b: boolean) => {
    try {
      await pp.lcoGenerator_update_use610ab(b);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };
  const update_usePsi1 = async (b: boolean) => {
    try {
      await pp.lcoGenerator_update_usePsi1(b);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };
  const update_selLCAs = async (n: string[]) => {
    try {
      await pp.lcoGenerator_update_selLCAs(n);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };
  const update_selLCOs = async (n: ng.loadcombination.Type[]) => {
    try {
      await pp.lcoGenerator_update_selLCOs(n);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };
  const update_inclPermLoadFavor = async (b: boolean) => {
    try {
      await pp.lcoGenerator_inclPermLoadFavor(b);
    } catch (x) {
      report_exception(t, x, t("lco_generator_.basic_snow_load_"));
    }
  };

  // ============================================================================================
  // values to display

  const altitude = ec?.altitude ?? 0;
  const snowLoad = ec?.snowLoad ?? 0;
  const selLCAs = cache?.loadCombGenSettings?.loadcases;
  const selLCOs = cache?.loadCombGenSettings?.lcTypes ?? [];
  const inclPermLoadFavor = cache?.loadCombGenSettings?.getSimple()?.includePermanentLoadFavorable ?? false;

  // ============================================================================================
  // dropdown options

  const optionsLCA = (() => {
    const lcas: { value: ValueType; label: string }[] = [];
    for (const tmpLca of loading?.loadcases ?? []) {
      if (tmpLca?.id) {
        lcas.push({
          value: tmpLca.id.guid ?? "",
          label: tmpLca.id.name ?? "",
        });
      }
    }
    return lcas;
  })();

  const optionsLCO = LOAD_COMBINATION_TYPES.map((tmpLco) => {
    return {
      value: tmpLco.id,
      label: t(`lco_generator_.lco_.${tmpLco.name}`) ?? "",
    };
  });

  // ============================================================================================
  // Compose UI

  const psi_1_label = "ψ₁"; // <span style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: '&psi;<sub>1</sub>' }} />
  const psi_2_label = "ψ₂"; // <span style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: '&psi;<sub>2</sub>' }} />

  const optionalRow =
    showAltitude || showSnowLoad || show610ab || showPsi1 ? (
      <Container id="ngtable" className="ps-0 pe-0 mb-3">
        <Row className="container-row align-items-end g-2">
          {showAltitude && (
            <Col xs={12} md="auto">
              <div className="editfield-label d-inline-flex align-items-center gap-2">
                <span>{t("lco_generator_.altitude_")}</span>
                <InfoIcon id="lco-altitude-tooltip" text={t("lco_generator_.altitude_info_")} />
              </div>
              {/* Sized to a metres-above-sea-level figure plus its unit, matching the snow load
                  field beside it - the two never show together, but they read as one control. */}
              <div style={{ maxWidth: 150 }}>
                <SXEditField
                  hover
                  id="altitudefield"
                  textAlign="left"
                  fontSize={12}
                  fullWidth
                  type="number"
                  onUpdate={(val: number) => {
                    update_alt(val);
                  }}
                  val={altitude}
                  unit={altitudeUnit}
                />
              </div>
            </Col>
          )}

          {showSnowLoad && (
            <Col xs={12} md="auto">
              <div className="editfield-label d-inline-flex align-items-center gap-2">
                <span>{t("lco_generator_.basic_snow_load_")}</span>
                <InfoIcon id="lco-snowload-tooltip" text={t("lco_generator_.basic_snow_load_info_")} />
              </div>
              {/* A basic snow load is a one-decimal number, so the field is sized to "2,5" plus its
                  unit rather than stretched across a third of the dialog. */}
              <div style={{ maxWidth: 150 }}>
                <SXEditField
                  hover
                  textAlign="left"
                  fontSize={12}
                  fullWidth
                  id={"snowloadfield"}
                  type="number"
                  onUpdate={(val: number) => {
                    update_snowload(val);
                  }}
                  val={snowLoad}
                  unit={snowLoadUnit}
                />
              </div>
            </Col>
          )}

          {show610ab && (
            <Col
              xs={12}
              md={4}
              className="d-flex align-items-center align-self-center ps-md-3"
              style={{ transform: "translateY(14px)" }}
            >
              <Form.Switch
                type="switch"
                className="sx-switch custom-switch mb-0"
                id={"use610abCheckbox"}
                name={"use610ab"}
                label={
                  <span className="d-inline-flex align-items-center gap-2">
                    <span>{t("lco_generator_.use_610ab_")}</span>
                    <InfoIcon id="lco-use610ab-tooltip" text={t("lco_generator_.use_610ab_info_")} />
                  </span>
                }
                checked={genDesSet_load?.use610ab ?? false}
                onChange={(e) => {
                  update_use610ab(e.target.checked as boolean);
                }}
              />
            </Col>
          )}

          {showPsi1 && (
            <Col
              xs={12}
              md="auto"
              // ps-md-4 keeps the toggle off the snow load field beside it - the two ran together
              // as one control at the Swedish annex, which is the one annex that shows both.
              className="d-flex align-items-center align-self-center ps-md-4"
              style={{ transform: "translateY(14px)" }}
            >
              <SXTwoWaySwitch
                leftLabel={psi_2_label}
                rightLabel={psi_1_label}
                checked={genDesSet_load?.usePsi1 ?? false}
                direction={t("lco_generator_.use_psi1_instead_of_psi2.dir") === "rtl" ? "rtl" : "ltr"}
                onChange={(x) => {
                  update_usePsi1(x);
                }}
              />
              <InfoIcon id="lco-usepsi1-tooltip" text={t("lco_generator_.use_psi1_info_")} />
            </Col>
          )}
        </Row>
      </Container>
    ) : null;

  const loadCaseRow = (
    <Row>
      <Col xs={12}>
        <Row>
          <DualListBox<string, boolean> //https://www.npmjs.com/package/react-dual-listbox
            className="sx-dual-listbox"
            icons={dualListBoxIcons}
            lang={dualListLang(t)}
            showHeaderLabels
            options={optionsLCA}
            selected={selLCAs}
            onChange={(e) => {
              update_selLCAs(e);
            }}
          />
        </Row>
        <Row className="container-row align-items-center">
          <Col xs={12} className="d-flex align-items-center gap-2">
            <Form.Check
              type="switch"
              className="sx-switch custom-switch"
              checked={inclPermLoadFavor}
              label={t("include_favorable_permanent_load_.label")}
              onChange={(e) => {
                update_inclPermLoadFavor(e.target.checked);
              }}
              id="favLoad"
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const loadCombRow = (
    <Row>
      <Col xs={12}>
        <Row>
          <DualListBox<ng.loadcombination.Type, boolean> //https://www.npmjs.com/package/react-dual-listbox
            className="sx-dual-listbox"
            icons={dualListBoxIcons}
            lang={dualListLang(t)}
            showHeaderLabels
            options={optionsLCO}
            selected={selLCOs}
            onChange={(e) => {
              update_selLCOs(e);
            }}
          />
        </Row>
      </Col>
    </Row>
  );

  const rootContainer = (
    <Container className="ps-0 pe-0">
      {optionalRow}
      <Row style={{ marginTop: 16, marginBottom: 8, borderBottom: "1px solid lightgray" }}>
        <Col className="header">{t("lco_generator_.loadcases_")}</Col>
      </Row>
      {loadCaseRow}
      <Row style={{ marginTop: 16, marginBottom: 8, borderBottom: "1px solid lightgray" }}>
        <Col className="header">{t("lco_generator_.loadcombs_")}</Col>
      </Row>
      {loadCombRow}
    </Container>
  );

  // ============================================================================================
  // Final UI
  return (
    <SXModalBase
      header={t("lco_generator_.title")}
      show={props.isModalOpen}
      size="lg"
      close={() => {
        dispatch(closeModal(LCOGeneratorModalId));
      }}
      body={
        <Container>
          <Row style={{ marginBottom: "3rem" }}>
            <Col xs={12}>{rootContainer}</Col>
          </Row>
        </Container>
      }
      footer={
        <>
          {pp.generateLoadCombsStatus == "loading" && <SXSpin size={"small"} />}
          <Button
            id="disclaimerok"
            className="primary-btn"
            onClick={() => {
              dispatch(closeModal(LCOGeneratorModalId));
            }}
          >
            {t("general.ok")}
          </Button>
        </>
      }
    />
  );
};
